import Head from "next/head";

import ResourcePostGrid from "@/components/blog/ResourcePostGrid";
import ResourcesPaginator from "@/components/blog/ResourcesPaginator";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { getResourcePostsMeta } from "@/lib/resources/loader";

import type { GetStaticProps } from "next";
import type { ResourcePostMeta } from "@/lib/resources/schema";

const POSTS_PER_PAGE = 15;

interface ResourcesIndexProps {
  posts: ResourcePostMeta[];
  totalPages: number;
  currentPage: number;
}

export default function ResourcesIndex({ posts, totalPages, currentPage }: ResourcesIndexProps) {
  const baseUrl = process.env.NEXT_PUBLIC_API_HOST || "https://rebolthq.com";
  const canonicalUrl = currentPage === 1 ? `${baseUrl}/resources` : `${baseUrl}/resources/page/${currentPage}`;

  return (
    <>
      <Head>
        <title>Resources & Blog | Rebolt</title>
        <meta
          name="description"
          content="Explore Rebolt's resources and blog for home service businesses. Get marketing tips, industry insights, and strategies to grow your service business."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          property="og:image"
          content="https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/resources&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800"
        />
      </Head>

      <NavbarLegacy />

      <main className="from-rebolt-700/10 to-rebolt-800/5 bg-linear-to-r">
        <div className="max-w-7xl mx-auto py-24 sm:py-32 ">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center py-14">
              <h2 className="text-3xl font-bold tracking-tight text-ash-900 sm:text-6xl">Resources &amp; Tips</h2>
              <p className="mt-2 text-lg leading-8 text-ash-600">
                Learn how to grow your home service business with expert advice.
              </p>
            </div>
            <ResourcePostGrid posts={posts} />
          </div>
          {totalPages > 1 && <ResourcesPaginator totalPages={totalPages} currentPage={currentPage} />}
        </div>
        <Footer />
      </main>
    </>
  );
}

export const getStaticProps: GetStaticProps<ResourcesIndexProps> = async () => {
  const { posts, totalPages } = getResourcePostsMeta(1, POSTS_PER_PAGE);

  return {
    props: {
      posts,
      totalPages,
      currentPage: 1,
    },
  };
};
